import { useState, useCallback, useEffect, useRef } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

export function CarouselRenderer({ element }: RendererProps) {
  const { dispatchEvent } = useRenderContext()
  const slides = element.children ?? []
  const slideCount = slides.length
  const [currentIndex, setCurrentIndex] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const goTo = useCallback(
    (index: number) => {
      const next = (index + slideCount) % slideCount
      setCurrentIndex(next)
      dispatchEvent(element.id, "slideChange", { index: next })
    },
    [element.id, slideCount, dispatchEvent],
  )

  const handlePrev = useCallback(() => {
    goTo(currentIndex - 1)
  }, [currentIndex, goTo])

  const handleNext = useCallback(() => {
    goTo(currentIndex + 1)
  }, [currentIndex, goTo])

  // Auto-advance when variant is "auto"
  useEffect(() => {
    if (element.variant === "auto" && slideCount > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          const next = (prev + 1) % slideCount
          dispatchEvent(element.id, "slideChange", { index: next })
          return next
        })
      }, 4000)
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current)
      }
    }
  }, [element.id, element.variant, slideCount, dispatchEvent])

  if (slideCount === 0) {
    return (
      <div
        className={cn(
          "flex h-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground",
          element.className,
        )}
        data-testid={`carousel-${element.id}`}
      >
        No slides
      </div>
    )
  }

  return (
    <div
      className={cn("relative w-full overflow-hidden", element.className)}
      data-testid={`carousel-${element.id}`}
    >
      {/* Slide track */}
      <div
        className="flex transition-transform duration-300 ease-in-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {slides.map((slide) => (
          <div key={slide.id} className="w-full shrink-0">
            <ElementRenderer element={slide} />
          </div>
        ))}
      </div>

      {/* Previous button */}
      {slideCount > 1 && (
        <button
          type="button"
          className="absolute left-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border bg-background/80 shadow-sm transition-colors hover:bg-accent"
          onClick={handlePrev}
          aria-label="Previous slide"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
      )}

      {/* Next button */}
      {slideCount > 1 && (
        <button
          type="button"
          className="absolute right-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border bg-background/80 shadow-sm transition-colors hover:bg-accent"
          onClick={handleNext}
          aria-label="Next slide"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      )}

      {/* Dot indicators */}
      {slideCount > 1 && (
        <div className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              className={cn(
                "h-2 w-2 rounded-full transition-colors",
                i === currentIndex ? "bg-primary" : "bg-primary/30",
              )}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
